import globalSearchApi from "../api/GlobalSearchApi";
import { onDomReady } from "../common/DomUtils";

const MAX_TERM_LEN = 200;
const MIN_CHARS = 2;
const DEBOUNCE_MS = 300;
const MAX_SUGGESTIONS = 8;

// State for debouncing and stale-response protection
let debounceHandle: number | null = null;
let lastQuery = "";
let requestSeq = 0;

function sanitizeTerm(raw: string | null | undefined): string {
  const v = (raw ?? "").trim();
  if (!v) return "";
  const noCrlf = v.replace(/[\r\n]+/g, " ");
  return noCrlf.length > MAX_TERM_LEN
    ? noCrlf.substring(0, MAX_TERM_LEN)
    : noCrlf;
}

// Build a safe, same-origin URL to the search page with the term in the hash
function buildSearchUrl(term: string): string {
  const safeTerm = sanitizeTerm(term);
  const url = new URL("/search/", window.location.origin);
  url.hash = `#?term=${encodeURIComponent(safeTerm)}`;
  return url.toString();
}

function performSearch(term: string) {
  const safeTerm = sanitizeTerm(term);
  if (!safeTerm) return;
  window.location.assign(buildSearchUrl(safeTerm));
}

function clearContainer(el: Element) {
  while (el.firstChild) {
    el.removeChild(el.firstChild);
  }
}

function renderSuggestions(
  container: HTMLUListElement,
  items: unknown[],
  searchInput: HTMLInputElement
) {
  clearContainer(container);

  const list = (Array.isArray(items) ? items : [])
    .map((x) => String(x).trim())
    .filter((x) => x.length > 0)
    .slice(0, MAX_SUGGESTIONS);

  if (list.length === 0) {
    container.setAttribute("hidden", "");
    return;
  }

  for (const x of list) {
    const li = document.createElement("li");
    li.textContent = x;
    li.addEventListener("click", () => {
      searchInput.value = x;
      container.setAttribute("hidden", "");
    });
    container.appendChild(li);
  }

  container.removeAttribute("hidden");
}

export const initializeSiteSearch = () => {
  onDomReady(() => {
    const searchInput = document.getElementById(
      "js-search-bar-input"
    ) as HTMLInputElement | null;
    const searchButtonElts = document.getElementsByClassName("search-bar__btn");
    const searchButton = (
      searchButtonElts && searchButtonElts.length > 0
        ? searchButtonElts[0]
        : null
    ) as HTMLElement | null;
    const searchSuggestions = document.getElementById(
      "js-search-bar-suggestions"
    ) as HTMLElement | null;
    const searchPlaceholder = document.getElementById(
      "js-search-bar-placeholder"
    ) as HTMLElement | null;
    const suggestionsContainer = document.getElementById(
      "awesomplete_list_1"
    ) as HTMLUListElement | null;

    if (!searchInput) {
      return;
    }

    // If URL has a ?term=, reflect it in the input; this is safe for input.value
    const searchTermFromUrl = new URLSearchParams(window.location.search).get(
      "term"
    );
    if (searchTermFromUrl) {
      searchInput.value = sanitizeTerm(searchTermFromUrl);
      if (searchSuggestions) searchSuggestions.style.display = "none";
      if (searchPlaceholder) searchPlaceholder.style.display = "none";
    }

    // Input handler with debounce and min length
    searchInput.addEventListener("input", () => {
      const searchTerm = sanitizeTerm(searchInput.value);

      // Toggle UI state
      if (searchTerm) {
        if (searchSuggestions) searchSuggestions.style.display = "none";
        if (searchPlaceholder) searchPlaceholder.style.display = "none";
        if (searchButton) {
          searchButton.style.display = "block";
          searchButton.style.opacity = "100";
        }
      } else {
        if (searchSuggestions) searchSuggestions.style.display = "block";
        if (searchPlaceholder) searchPlaceholder.style.display = "block";
        if (searchButton) {
          searchButton.style.display = "none";
        }
      }

      // No suggestions container or below min chars: hide suggestions and stop
      if (!suggestionsContainer || searchTerm.length < MIN_CHARS) {
        if (suggestionsContainer) {
          clearContainer(suggestionsContainer);
          suggestionsContainer.setAttribute("hidden", "");
        }
        return;
      }

      // Debounce remote call
      if (debounceHandle) {
        clearTimeout(debounceHandle);
      }
      debounceHandle = window.setTimeout(async () => {
        // Avoid duplicate fetch for unchanged query
        if (searchTerm === lastQuery) return;
        lastQuery = searchTerm;

        const seq = ++requestSeq;

        try {
          // Prefer suggest(), fallback to autocomplete()
          const results = await globalSearchApi.suggest(lastQuery);
            

          // Ignore stale responses
          if (seq !== requestSeq) return;

          renderSuggestions(suggestionsContainer, results, searchInput);
        } catch {
          // On error, hide suggestions
          suggestionsContainer.setAttribute("hidden", "");
        }
      }, DEBOUNCE_MS);
    });

    // Enter to search
    searchInput.addEventListener("keyup", (event) => {
      if (event.key === "Enter") {
        performSearch(searchInput.value);
      }
      // ESC to hide suggestions
      if (event.key === "Escape" && suggestionsContainer) {
        suggestionsContainer.setAttribute("hidden", "");
      }
    });

    // Click to search
    if (searchButton) {
      searchButton.addEventListener("click", () => {
        performSearch(searchInput.value);
      });
    }

    // Click-outside to close suggestions
    document.addEventListener("click", (e) => {
      if (!suggestionsContainer) return;
      const target = e.target as Node | null;
      if (
        target &&
        target !== searchInput &&
        !suggestionsContainer.contains(target)
      ) {
        suggestionsContainer.setAttribute("hidden", "");
      }
    });
  });
};
